// frontend/modules/service-metrics/components/ServiceLogsTable.tsx
import React from 'react';
import { Card, Table, Tag, Typography, Tooltip } from 'antd';
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    StopOutlined,
    ThunderboltOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import type { ServiceMetricLogDTO } from '@/shared/api/model';
import type { MetricStatus } from '../types/service-metrics.types';

const { Text } = Typography;

interface ServiceLogsTableProps {
    data: ServiceMetricLogDTO[];
    loading?: boolean;
    title?: string;
    pagination?: {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, pageSize: number) => void;
    };
}

const getStatusConfig = (status: MetricStatus) => {
    switch (status) {
        case 'SUCCESS':
            return { color: 'success', icon: <CheckCircleOutlined />, text: 'Success' };
        case 'FAILURE':
            return { color: 'error', icon: <CloseCircleOutlined />, text: 'Failed' };
        case 'RATE_LIMITED':
            return { color: 'warning', icon: <StopOutlined />, text: 'Rate Limited' };
        case 'CIRCUIT_BREAKER_OPEN':
            return { color: 'error', icon: <ThunderboltOutlined />, text: 'CB Open' };
        case 'TIMEOUT':
            return { color: 'warning', icon: <ClockCircleOutlined />, text: 'Timeout' };
        default:
            return { color: 'default', icon: null, text: status };
    }
};

const ServiceLogsTable: React.FC<ServiceLogsTableProps> = ({ 
    data, 
    loading,
    title = 'Recent Logs',
    pagination
}) => {
    const { dark } = useTheme();

    const columns = [
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (value: string) => new Date(value).toLocaleString(),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: MetricStatus) => {
                const config = getStatusConfig(status);
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
            },
        },
        {
            title: 'Response Time',
            dataIndex: 'responseTimeMs',
            key: 'responseTimeMs',
            width: 120,
            render: (value: number) => (
                <span>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {value}ms
                </span>
            ),
        },
        {
            title: 'User',
            dataIndex: 'userId',
            key: 'userId',
            width: 120,
            render: (value: string) => value || <Text type="secondary">-</Text>,
        },
        {
            title: 'Branch',
            dataIndex: 'branch',
            key: 'branch',
            width: 100,
            render: (value: string) => value || <Text type="secondary">-</Text>,
        },
        {
            title: 'Customer',
            dataIndex: 'custNo',
            key: 'custNo',
            width: 100,
            render: (value: string) => value || <Text type="secondary">-</Text>,
        },
        {
            title: 'Error',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            ellipsis: true,
            render: (value: string, record: ServiceMetricLogDTO) => (
                value ? (
                    <Tooltip title={value}>
                        <Text type="danger" ellipsis style={{ maxWidth: 200 }}>
                            [{record.errorType}] {value}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">-</Text>
                )
            ),
        },
        {
            title: 'Request ID',
            dataIndex: 'requestId',
            key: 'requestId',
            width: 150,
            ellipsis: true,
            render: (value: string) => (
                <Tooltip title={value}>
                    <Text copyable={{ text: value }} style={{ fontSize: 12 }}>
                        {value?.substring(0, 8)}...
                    </Text>
                </Tooltip>
            ),
        },
    ];

    return (
        <Card 
            title={title} 
            loading={loading}
            className={dark ? 'bg-gray-800' : ''}
        >
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                pagination={pagination ? {
                    current: pagination.current + 1,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: (page, size) => pagination.onChange(page - 1, size),
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} records`,
                } : { pageSize: 10 }}
                size="small"
                scroll={{ x: 1200 }}
            />
        </Card>
    );
};

export default ServiceLogsTable;
